import React, { useMemo } from 'react'

import { getIntegrationDisplayName, IntegrationServiceId, isContactCreateEnabledInConfig } from '../../../common/integrations'
import { useAccount } from '../../../common/useAccount'
import { getEffectiveAccountIntegrationConfig } from '../../../models/Account'
import { useIntegrations } from '../../Integration/useIntegrations'

import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import ProspectingSuccessLogo from '../../../assets/images/prospecting-success-logo.png'

import { Button } from '../../../components/Button/Button'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  clearDocumentProspectingDetailsForm,
  selectDocumentProspectingAttachment,
  selectDocumentProspectingContact,
  selectDocumentProspectingCurrentUser,
  selectDocumentProspectingServiceId,
  selectDocumentProspectingSuccessIntegrations,
  setDocumentProspectingServiceId,
} from '../../../store/processDocument/documentProspecting'

import { useShowSummaryForDocumentProspecting } from './hooks'

interface ProspectingSuccessProps {
  onRestart: () => void
}

export const ProspectingSuccess: React.FC<ProspectingSuccessProps> = ({ onRestart }) => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const successIntegrations = useAppSelector(selectDocumentProspectingSuccessIntegrations)
  const serviceId = useAppSelector(selectDocumentProspectingServiceId)
  const currentUser = useAppSelector(selectDocumentProspectingCurrentUser)
  const contact = useAppSelector(selectDocumentProspectingContact)
  const attachment = useAppSelector(selectDocumentProspectingAttachment)

  const { showSummary, isProcessing } = useShowSummaryForDocumentProspecting()

  const { account } = useAccount()
  const { supportsDocumentContactUpdate } = useIntegrations()

  const integrations = useMemo(
    () =>
      currentUser?.integrations
        ?.filter(({ serviceId }) => supportsDocumentContactUpdate(serviceId))
        .filter(({ serviceId }) => !successIntegrations.includes(serviceId))
        .filter(({ serviceId }) => isContactCreateEnabledInConfig(getEffectiveAccountIntegrationConfig(account, serviceId))) ?? [],
    [currentUser, successIntegrations, account, supportsDocumentContactUpdate],
  )

  const _handleIntegrationSelected = (selectedServiceId: IntegrationServiceId) => {
    dispatch(clearDocumentProspectingDetailsForm())
    dispatch(setDocumentProspectingServiceId(selectedServiceId))
    onRestart()
  }

  const handleIntegrationSelected = tracked(
    _handleIntegrationSelected,
    { eventName: TrackEvent.DOCUMENT_PROSPECTING_EXTERNAL_SYSTEM_SELECTED },
    { eventDetailsExtractor: (selectedServiceId: IntegrationServiceId) => ({ targetSystem: selectedServiceId }) },
  )

  if (!contact || !attachment) {
    return null
  }

  const integrationDisplayName = serviceId ? getIntegrationDisplayName(serviceId) : null
  const hasIntegrationOptions = integrations.length > 0

  return (
    <div className='d-flex flex-column align-items-center zocks-gap-2 document-prospecting-success-container'>
      <img src={ProspectingSuccessLogo} alt='Prospecting Success' />
      <div className='d-flex flex-column align-items-center gap-2'>
        <h5>Contact created successfully</h5>
        <p>
          <span className='paragraph-medium'>{getUserDisplayName(contact)} has been added to</span>{' '}
          <span className='paragraph-medium zocks-text-primary'>Zocks</span>
          {integrationDisplayName ? (
            <>
              {' '}
              <span className='paragraph-medium'>and</span> <span className='paragraph-medium zocks-text-primary'>{integrationDisplayName}</span>
            </>
          ) : null}
        </p>
      </div>
      <div className='d-flex flex-column align-items-center py-3 gap-4 zocks-border-0.75 zocks-bg-color-gray-100 w-100'>
        <h5 className='text-center px-3'>
          {hasIntegrationOptions ? 'What would you like to do next?' : 'Summarize the document to add its details to the contact'}
        </h5>
        <div className='d-flex align-items-center justify-content-center flex-wrap gap-2 px-3'>
          <Button variant='primary' className='button-medium px-3 py-2' loading={isProcessing} onClick={showSummary}>
            Get document summary
          </Button>
          {hasIntegrationOptions
            ? integrations.map(({ serviceId }) => (
                <div
                  key={serviceId}
                  className='px-3 py-2 button-medium zocks-text-gray-900 zocks-bg-color-white rounded-1 border zocks-border-color-gray-300 cursor-pointer'
                  onClick={() => handleIntegrationSelected(serviceId)}
                >
                  Add contact to {getIntegrationDisplayName(serviceId)}
                </div>
              ))
            : null}
        </div>
        <a
          className='paragraph-medium zocks-text-primary bold cursor-pointer'
          href='/dashboard/library/manage-contacts'
          target='_blank'
          rel='noopener noreferrer'
        >
          Go back to Contacts
        </a>
      </div>
    </div>
  )
}
